import { almacenApi } from '@/features/almacen/api/almacen.api'
import { apiService } from '@/services/core/api.service'

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface Herramienta {
  id: string
  nombre: string
  status: 'good' | 'new_equipment' | 'damaged' | 'in_maintenance' | 'missing'
  photoUrl: string | null
  registradaEn: string // ISO date
}

export interface AlertaMantenimiento {
  id: string
  herramienta: string
  tipo: 'vencido' | 'por_vencer'
  // Solo cuando el intervalo de mantenimiento es por horas de uso:
  horasAcumuladas?: number
  intervaloHoras?: number
  // Solo cuando el intervalo de mantenimiento es por calendario:
  fechaLimite?: string   // YYYY-MM-DD
  diasRestantes?: number // negativo si ya venció
  photoUrl: string | null
}

export interface MovimientoHistorial {
  id: string
  herramienta: string
  responsable: string
  proyectoAsignado: string
  fechaHora: string // ISO date-time
  tipo: 'assignment' | 'return' | 'reassignment'
}

export interface AlmacenMetrics {
  totalHerramientas: number
  enBuenEstado: number
  conReporteDano: number
  enMantenimiento: number
}

export interface AlmacenDashboardData {
  metrics: AlmacenMetrics
  alertas: AlertaMantenimiento[]
  historialReciente: MovimientoHistorial[]
  herramientasRecientes: Herramienta[]
}

// ─── Empty state (used on error so no fake data is shown) ────────────────────

const EMPTY_DATA: AlmacenDashboardData = {
  metrics: { totalHerramientas: 0, enBuenEstado: 0, conReporteDano: 0, enMantenimiento: 0 },
  alertas: [],
  historialReciente: [],
  herramientasRecientes: [],
}

// ─── Service ──────────────────────────────────────────────────────────────────

class AlmacenService {
  async getDashboardData(almacenId?: string): Promise<AlmacenDashboardData> {
    // El error se deja subir a proposito. Antes se atrapaba y se devolvia
    // EMPTY_DATA, asi que un fallo se veia igual que "no hay datos" y el
    // dashboard decia "Sin alertas" sin que nadie se enterara. Quien llama
    // decide como avisarlo.
    const params = almacenId ? `?almacenId=${almacenId}` : ''
    const apiData = await apiService.request<AlmacenDashboardData>(
      `/almacen/dashboard${params}`,
    )
    return {
      metrics:              apiData.metrics              ?? EMPTY_DATA.metrics,
      alertas:              apiData.alertas              ?? [],
      historialReciente:    apiData.historialReciente    ?? [],
      herramientasRecientes: apiData.herramientasRecientes ?? [],
    }
  }

  /**
   * Herramientas que el usuario puede ver. El back acota este listado por
   * permisos: con solo "ver_asignadas" devuelve unicamente las suyas.
   */
  async getHerramientasVisibles(): Promise<{ status: string }[]> {
    try {
      const res = await apiService.request<{ data: { status: string }[] }>(
        '/almacen/herramientas?limit=100',
      )
      return res.data ?? []
    } catch {
      return []
    }
  }

  async getAlmacenes(): Promise<{ id: string; nombre: string }[]> {
    try {
      const warehouses = await almacenApi.findAll()
      return warehouses
        .filter((w) => w.isActive)
        .map((w) => ({ id: w.id, nombre: w.name }))
    } catch {
      return []
    }
  }
}

export const almacenService = new AlmacenService()
